import React from 'react';
import { authorAuthService } from '../services/authorAuth';
import { FileText, Calendar, User, BookOpen, Link as LinkIcon, Download, Clock } from 'lucide-react';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl, getExtensionFromUrl } from '../../../utils/downloadHelper';

interface SubmissionDetailsProps {
    submissionId: string;
}

const SubmissionDetails: React.FC<SubmissionDetailsProps> = ({ submissionId }) => {
    const [submission, setSubmission] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(true);
    const [downloading, setDownloading] = React.useState(false);

    React.useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            try {
                const res = await authorAuthService.getSubmissionById(submissionId);
                if (res.isSuccess && res.data) setSubmission(res.data);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [submissionId]);

    if (loading) return (
        <div className="flex justify-center items-center py-20">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
        </div>
    );

    if (!submission) return <p className="text-gray-500 text-center py-10">Unable to load submission details.</p>;

    const getApplicationType = (type: number) => type === 1 ? 'Book' : type === 2 ? 'Journal' : 'Other';
    const isJournal = submission.applicationType === 2;

    // Filter workflow history to only stages involving the author
    const authorTrail = (submission.workflowHistories || []).filter(
        (h: any) =>
            h.fromStage?.toLowerCase().includes('author') ||
            h.toStage?.toLowerCase().includes('author')
    );

    const manuscriptUrl = submission.versions?.length > 0
        ? submission.versions[submission.versions.length - 1].manuscriptUrl
        : null;

    // For journals, the bookAuthors array carries editorial board members instead.
    const editorialBoardMembers = isJournal ? (submission.bookAuthors ?? []) : [];

    const authorsDisplay = (submission.authors && String(submission.authors).trim() !== '')
        ? submission.authors
        : (!isJournal && submission.bookAuthors && submission.bookAuthors.length > 0
            ? submission.bookAuthors.map((a: any) => a?.name ?? '').filter(Boolean).join(', ')
            : 'N/A');

    const formatAccessibility = (v?: string) =>
        v === 'Fully open' || v === 'Partially' || v === 'Not available' ? v : (v || 'N/A');

    const formatCommencementDate = (s?: string) => {
        if (!s) return 'N/A';
        try {
            return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch { return 'N/A'; }
    };

    const absUrl = (u?: string) => (!u ? '' : u.startsWith('http') ? u : `${FILE_BASE_URL}/${u.replace(/^\//, '')}`);

    return (
        <div className="space-y-8">
            {/* Submission Details */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-[#088A61]" />
                    {isJournal ? 'Journal Details' : 'Manuscript Details'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="col-span-full">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                            {isJournal ? 'Name of Journal' : 'Title'}
                        </label>
                        <p className="text-gray-900 font-medium text-lg">{submission.title}</p>
                    </div>
                    <div className="col-span-full">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                            {isJournal ? 'Preface / Description' : 'Abstract'}
                        </label>
                        <p className="text-gray-600 leading-relaxed">{submission.abstract}</p>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                            {isJournal ? 'Name of Journal Leader' : 'Authors'}
                        </label>
                        <p className="text-gray-900 flex items-center gap-2"><User size={16} className="text-gray-400" />{authorsDisplay}</p>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Thematic Area</label>
                        <p className="text-gray-900">{submission.category?.name || submission.categoryName || 'N/A'}</p>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Date Submitted</label>
                        <p className="text-gray-900 flex items-center gap-2">
                            <Calendar size={16} className="text-gray-400" />
                            {new Date(submission.submittedAt).toLocaleDateString()}
                        </p>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Type</label>
                        <p className="text-gray-900">{getApplicationType(submission.applicationType)}</p>
                    </div>
                    {!isJournal && submission.edition && (
                        <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Edition</label>
                            <p className="text-gray-900">{submission.edition}</p>
                        </div>
                    )}
                    {isJournal && (
                        <>
                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Position of Journal Representative</label>
                                <p className="text-gray-900">{submission.positionOfJournalRepresentative || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Journal Professional Association</label>
                                <p className="text-gray-900">{submission.journalProfessionalAssociation || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Current Volume</label>
                                <p className="text-gray-900">{submission.volume || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Current Issue</label>
                                <p className="text-gray-900">{submission.issue || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Date of Journal Commencement</label>
                                <p className="text-gray-900">{formatCommencementDate(submission.dateOfJournalCommencement)}</p>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Article Accessibility</label>
                                <p className="text-gray-900">{formatAccessibility(submission.journalBodyArticleAccessibility)}</p>
                            </div>
                            <div className="col-span-full">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Physical Address</label>
                                <p className="text-gray-900">{submission.journalBodyPhysicalAddress || 'N/A'}</p>
                            </div>
                            <div className="col-span-full">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Journal URL</label>
                                {submission.publicationUrl ? (
                                    <a href={submission.publicationUrl} target="_blank" rel="noopener noreferrer" className="text-[#088A61] hover:underline flex items-center gap-1">
                                        <LinkIcon size={14} />{submission.publicationUrl}
                                    </a>
                                ) : <p className="text-gray-900">N/A</p>}
                            </div>
                            <div className="col-span-full">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Association Body Website</label>
                                {submission.journalBodyWebsite ? (
                                    <a href={submission.journalBodyWebsite} target="_blank" rel="noopener noreferrer" className="text-[#088A61] hover:underline flex items-center gap-1">
                                        <LinkIcon size={14} />{submission.journalBodyWebsite}
                                    </a>
                                ) : <p className="text-gray-900">N/A</p>}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Editorial Board Members (journals only) */}
            {isJournal && editorialBoardMembers.length > 0 && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <User className="w-5 h-5 text-[#088A61]" />
                        Editorial Board Members ({editorialBoardMembers.length})
                    </h3>
                    <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                        <ol className="space-y-2">
                            {editorialBoardMembers.map((m: any, i: number) => (
                                <li key={m.id ?? i} className="text-sm text-gray-900 leading-relaxed">
                                    <span className="font-semibold">{i + 1}. {m.name ?? '—'}</span>
                                    {m.affiliateInstitution && <span className="text-gray-600"> — {m.affiliateInstitution}</span>}
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            )}

            {/* Supporting documents (journals) */}
            {isJournal && (submission.journalBodyOperationalGuideLineFileUrl || submission.journalBodyPolicyFileUrl) && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#088A61]" />
                        Supporting Documents
                    </h3>
                    <div className="space-y-3">
                        {submission.journalBodyOperationalGuideLineFileUrl && (
                            <a
                                href={absUrl(submission.journalBodyOperationalGuideLineFileUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-100 hover:border-[#088A61] hover:bg-[#f0fdf4] transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-[#088A61]/10 rounded-lg text-[#088A61]"><FileText size={20} /></div>
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">Publication & Operational Guidelines</p>
                                        <p className="text-xs text-gray-500">Click to open</p>
                                    </div>
                                </div>
                                <Download size={16} className="text-gray-400" />
                            </a>
                        )}
                        {submission.journalBodyPolicyFileUrl && (
                            <a
                                href={absUrl(submission.journalBodyPolicyFileUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-100 hover:border-[#088A61] hover:bg-[#f0fdf4] transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-[#088A61]/10 rounded-lg text-[#088A61]"><FileText size={20} /></div>
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">Journal Policy</p>
                                        <p className="text-xs text-gray-500">Click to open</p>
                                    </div>
                                </div>
                                <Download size={16} className="text-gray-400" />
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* Manuscript Download */}
            {manuscriptUrl && (
                <div className="bg-[#f0fdf4] border border-green-100 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg text-green-600"><FileText size={20} /></div>
                        <div>
                            <h4 className="font-medium text-gray-900">{isJournal ? 'Journal File' : 'Manuscript File'}</h4>
                            <p className="text-sm text-gray-500">Click to download</p>
                        </div>
                    </div>
                    <button
                        disabled={downloading}
                        onClick={async () => {
                            setDownloading(true);
                            const url = manuscriptUrl.startsWith('http') ? manuscriptUrl : `${FILE_BASE_URL}/${manuscriptUrl}`;
                            const ext = getExtensionFromUrl(manuscriptUrl) || '.docx';
                            const filename = `${(authorsDisplay || 'author')}_${submission.title || 'manuscript'}_${submission.applicationType === 1 ? 'Book' : 'Journal'}`
                                .replace(/[^a-z0-9]/gi, '_').toLowerCase() + ext;
                            try { await downloadFileFromUrl(url, filename); } finally { setDownloading(false); }
                        }}
                        className={`px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2 ${downloading ? 'opacity-75 cursor-not-allowed' : ''}`}
                    >
                        <Download size={16} className={downloading ? 'animate-pulse' : ''} />
                        {downloading ? 'Downloading...' : 'Download'}
                    </button>
                </div>
            )}

            {/* Author Workflow Trail */}
            {authorTrail.length > 0 && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-[#088A61]" />
                        Submission Trail
                    </h3>
                    <div className="space-y-3">
                        {authorTrail.map((h: any, i: number) => (
                            <div key={i} className="flex gap-3 items-start">
                                <div className="mt-1 w-2.5 h-2.5 rounded-full bg-[#088A61] shrink-0" />
                                <div className="bg-gray-50 rounded-lg p-3 flex-1">
                                    <p className="text-sm font-medium text-gray-800">
                                        {h.fromStage} → {h.toStage}
                                    </p>
                                    {h.actionType && <p className="text-xs text-gray-500 mt-0.5">Action: {h.actionType}</p>}
                                    {h.comment && <p className="text-xs text-gray-600 mt-1 italic">"{h.comment}"</p>}
                                    <p className="text-xs text-gray-400 mt-1">{new Date(h.actionAt).toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubmissionDetails;
